import {ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {Observable, catchError, finalize, forkJoin, map, of, switchMap} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {FormControl} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {PageEvent} from '@angular/material/paginator';
import {ManagementService} from './management.service';
import {StateService} from '../../services/state.service';
import {UtilService} from '../../services/util.service';
import {AuthorizationService} from '../../services/authorization.service';
import {RoutePaths} from '../../shared/types/common';
import {
  Account,
  AiTopicsResponse,
  Brand,
  BrandsResponse,
  ManagementRequest,
  SortState,
  User,
  UsersBrands
} from './types';
import {SearchBarComponent} from '../../shared/components/search-bar/search-bar.component';
import {ButtonComponent} from '../../shared/components/button/button.component';
import {PaginatorComponent} from '../../shared/components/paginator/paginator.component';
import {AccountsTableComponent} from './accounts-table/accounts-table.component';
import {UsersTableComponent} from './users-table/users-table.component';
import {BrandsTableComponent} from './brands-table/brands-table.component';
import {AccountModalComponent} from './account-modal/account-modal.component';
import {UserModalComponent} from './user-modal/user-modal.component';
import {BrandModalComponent} from './brand-modal/brand-modal.component';

type TableRow = Account | User | Brand;

interface TableQuery {
  tab: string;
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  term: string;
}

const PAGE_SIZE: Record<string, number> = {accounts: 10, users: 10, brands: 15};
const ADD_LABEL: Record<string, string> = {accounts: '+ Add Account', users: '+ Add User', brands: '+ Add Brand'};

@Component({
  selector: 'app-management-screen',
  imports: [
    SearchBarComponent,
    ButtonComponent,
    PaginatorComponent,
    AccountsTableComponent,
    UsersTableComponent,
    BrandsTableComponent,
  ],
  templateUrl: './management-screen.component.html',
  styleUrl: './management-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagementScreenComponent {
  public readonly tabs = ['accounts', 'users', 'brands'];

  // --- query state (single source of truth) ---
  private readonly tab = signal<string>('');
  private readonly page = signal(0);
  private readonly sortBy = signal('');
  private readonly sortDir = signal<'asc' | 'desc'>('desc');
  private readonly term = signal('');
  private readonly reloadTrigger = signal(0);

  // --- view state ---
  protected readonly rows = signal<TableRow[]>([]);
  protected readonly total = signal(0);
  protected readonly error = signal(false);
  protected readonly searchControl = new FormControl<string>('', {nonNullable: true});

  protected readonly activeTab = this.tab.asReadonly();
  protected readonly pageSize = computed(() => PAGE_SIZE[this.tab()] ?? PAGE_SIZE['accounts']);
  protected readonly addButtonText = computed(() => ADD_LABEL[this.tab()] ?? '');
  protected readonly paginatorPage = computed(() => this.page() + 1);

  protected readonly accountRows = computed(() => this.rows() as Account[]);
  protected readonly userRows = computed(() => this.rows() as User[]);
  protected readonly brandRows = computed(() => this.rows() as Brand[]);

  private readonly query = computed<TableQuery>(() => {
    this.reloadTrigger(); // re-run the fetch on an explicit reload (e.g. after add), even if params are unchanged
    return {
      tab: this.tab(),
      page: this.page(),
      pageSize: this.pageSize(),
      sortBy: this.sortBy(),
      sortDir: this.sortDir(),
      term: this.term(),
    };
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly managementService = inject(ManagementService);
  private readonly stateService = inject(StateService);
  private readonly utilService = inject(UtilService);
  private readonly authorizationService = inject(AuthorizationService);

  constructor() {
    // The active tab is driven reactively by the route param — no snapshot reads, so it never lags navigation.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const tab = params.get('tab') ?? '';
      if (!this.tabs.includes(tab)) {
        this.router.navigate(['/', RoutePaths.MANAGE, this.tabs[0]], {replaceUrl: true});
        return;
      }
      if (tab !== this.tab()) {
        this.tab.set(tab);
        this.resetTabState();
      }
    });

    // One cancelable load pipeline. switchMap cancels a stale request when the query changes;
    // finalize keeps the (reference-counted) loading flag balanced even on cancellation; catchError
    // surfaces the failure without tearing down the stream, so retries keep working.
    toObservable(this.query)
      .pipe(
        switchMap(query => {
          if (!query.tab) {
            return of(null);
          }
          this.stateService.loading = true;
          this.error.set(false);
          return this.fetchFor(query).pipe(
            catchError(err => {
              console.error('Error loading table data', err);
              this.error.set(true);
              this.utilService.openSnackBar(`Couldn't load ${query.tab}. Please try again.`, {
                position: 'center',
                variant: 'error'
              });
              return of(null);
            }),
            finalize(() => (this.stateService.loading = false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        if (result) {
          this.rows.set(result.rows);
          this.total.set(result.total);
        }
      });

    this.loadReferenceData();
  }

  // --- user actions ---

  switchTab(tab: string): void {
    if (tab === this.tab()) {
      return;
    }
    this.router.navigate(['/', RoutePaths.MANAGE, tab]);
  }

  onSearch(term: string): void {
    this.page.set(0);
    this.term.set(term ?? '');
  }

  onPageChange(event: PageEvent): void {
    this.page.set(event.pageIndex);
  }

  onSortChanged(sort: SortState): void {
    this.sortBy.set(sort.sortBy);
    this.sortDir.set(sort.sortDir);
  }

  retry(): void {
    this.reload();
  }

  onRowClicked(row: TableRow): void {
    switch (this.tab()) {
      case 'accounts':
        this.editAccount(row as Account);
        break;
      case 'users':
        this.editUser(row as User);
        break;
      case 'brands':
        this.editBrand(row as Brand);
        break;
    }
  }

  add(): void {
    switch (this.tab()) {
      case 'accounts':
        this.addAccount();
        break;
      case 'users':
        this.addUser();
        break;
      case 'brands':
        this.addBrand();
        break;
    }
  }

  // --- data flow ---

  private fetchFor(query: TableQuery): Observable<{ rows: TableRow[]; total: number }> {
    const params: Partial<ManagementRequest> = {
      limit: query.pageSize,
      offset: query.page * query.pageSize,
      term: query.term,
      sortBy: query.sortBy,
      sortDir: query.sortDir,
    };
    switch (query.tab) {
      case 'users':
        return this.managementService.getUsers(params).pipe(map(res => ({
          rows: res.users as TableRow[],
          total: res.total
        })));
      case 'brands':
        return this.managementService.getBrands({
          ...params,
          elaborate: true
        }).pipe(map(res => ({rows: res.brands as TableRow[], total: res.total})));
      case 'accounts':
        return this.managementService.getAccounts({
          ...params,
          elaborate: true
        }).pipe(map(res => ({rows: res.accounts as TableRow[], total: res.total})));
      default:
        return of({rows: [], total: 0});
    }
  }

  // Cross-tab reference data (roles, brand/account summaries, AI topics) for the modals' dropdowns,
  // loaded once on init. After mutations each flow refreshes only the list that can actually change
  // (brand add/edit → updateBrandInList/fetchAndStoreBrands; account add/edit → fetchAndStoreAccounts),
  // so we don't re-fan-out all four here. Each branch degrades to an empty list so one failure can't
  // blank the others.
  private loadReferenceData(): void {
    forkJoin({
      userRoles: this.managementService.getUserRoles().pipe(catchError(() => of({roles: []}))),
      brands: this.managementService.getBrands({elaborate: false}).pipe(catchError(() => of({
        brands: [],
        total: 0
      } as BrandsResponse))),
      accounts: this.managementService.getAccounts({elaborate: false}).pipe(catchError(() => of({
        accounts: [],
        total: 0
      }))),
      aiTopics: this.managementService.getAiTopics().pipe(catchError(() => of({
        topics: [],
        total: 0
      } as AiTopicsResponse))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({userRoles, brands, accounts, aiTopics}) => {
        this.managementService.userRoles = userRoles.roles;
        this.managementService.brandsList = brands.brands;
        this.managementService.accountList = accounts.accounts;
        this.managementService.aiTopics = aiTopics.topics;
      });
  }

  private resetTabState(): void {
    this.page.set(0);
    this.sortBy.set('');
    this.sortDir.set('desc');
    this.term.set('');
    this.searchControl.setValue('', {emitEvent: false});
    // Clear immediately so the newly-shown table never renders the previous tab's rows (different
    // shape) against its columns. Page/sort/search within a tab keep the rows for a smooth update.
    this.rows.set([]);
    this.total.set(0);
    this.error.set(false);
  }

  private reload(): void {
    this.reloadTrigger.update(value => value + 1);
  }

  private replaceRow(match: (row: TableRow) => boolean, updated: TableRow): boolean {
    const rows = this.rows();
    const index = rows.findIndex(match);
    if (index === -1) {
      return false;
    }
    const updatedRows = [...rows];
    updatedRows[index] = updated;
    this.rows.set(updatedRows);
    return true;
  }

  // --- add flows ---

  private addAccount(): void {
    const dialogRef = this.dialog.open(AccountModalComponent, {panelClass: 'management-modal', autoFocus: false});
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: Account) => {
      if (result) {
        this.searchControl.setValue('', {emitEvent: false});
        this.term.set('');
        this.page.set(0);
        this.reload();
        this.utilService.openSnackBar(`Account "${result.name}" added successfuly`, {
          position: 'center',
          variant: 'success'
        });
        // Balance the loading flag the modal raised on submit. Inside `if (result)` so a cancel
        // (no submit, no increment) doesn't over-decrement the shared counter.
        this.stateService.loading = false;
      }
    });
  }

  private addUser(): void {
    const dialogRef = this.dialog.open(UserModalComponent, {panelClass: 'management-modal', autoFocus: false});
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: User) => {
      if (result) {
        this.searchControl.setValue('', {emitEvent: false});
        this.term.set('');
        this.page.set(0);
        this.reload();
        this.utilService.openSnackBar(`User "${result.firstName}" added successfuly`, {
          position: 'center',
          variant: 'success'
        });
        // Balance the loading flag the modal raised on submit (previously missing here, which left
        // the global spinner stuck after a user add). Inside `if (result)` so a cancel doesn't over-decrement.
        this.stateService.loading = false;
      }
    });
  }

  private addBrand(): void {
    const dialogRef = this.dialog.open(BrandModalComponent, {panelClass: 'management-modal', autoFocus: false});
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: Brand) => {
      if (result) {
        this.searchControl.setValue(result.name, {emitEvent: false});
        this.term.set(result.name);
        this.page.set(0);
        this.reload();
        // Keeps brandsList (competitor dropdown) fresh; the modal also calls fetchAndStoreBrands.
        this.managementService.updateBrandInList({name: result.name, brandKey: result.brandKey});
        this.utilService.openSnackBar(`Brand "${result.name}" added successfuly`, {
          position: 'center',
          variant: 'success'
        });
        this.stateService.loading = false;
      }
    });
  }

  // --- edit flows (row click) ---

  private editAccount(account: Account): void {
    const dialogRef = this.dialog.open(AccountModalComponent, {
      data: {...account},
      panelClass: 'management-modal',
      autoFocus: false
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: Account & { deleted?: boolean }) => {
      if (result?.deleted) {
        // An account was removed — refresh the summaries the user-modal's account dropdown reads from,
        // and refetch so the row is gone and paging/total stay correct.
        this.managementService.fetchAndStoreAccounts();
        this.reload();
        this.utilService.openSnackBar(`Account "${result.name}" deleted successfully`, {
          position: 'center',
          variant: 'success'
        });
        this.stateService.loading = false;
        return;
      }
      if (result) {
        // A rename / brand-change isn't reflected anywhere else, so refresh the account summaries
        // the user-modal's account dropdown reads from. (Account add is already covered by the modal.)
        this.managementService.fetchAndStoreAccounts();
        const found = this.replaceRow(row => (row as Account).accountKey === result.accountKey, result);
        if (found) {
          this.utilService.openSnackBar(`Account "${result.name}" updated successfuly`, {
            position: 'center',
            variant: 'success'
          });
        }
        this.stateService.loading = false;
      }
    });
  }

  private editUser(user: User): void {
    const dialogRef = this.dialog.open(UserModalComponent, {
      data: {...user, brandKeys: user.brands.map((brand: UsersBrands) => brand.key)},
      panelClass: 'management-modal',
      autoFocus: false,
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: User & { deleted?: boolean }) => {
      if (result?.deleted) {
        // Refetch so the row is gone and paging/total stay correct.
        this.reload();
        this.utilService.openSnackBar(`User "${result.firstName}" deleted successfully`, {
          position: 'center',
          variant: 'success'
        });
        this.stateService.loading = false;
        return;
      }
      if (result) {
        const found = this.replaceRow(row => (row as User).id === result.id, result);
        if (found) {
          if (this.authorizationService.userProfile.email === result.email) {
            this.authorizationService.userProfile = {
              email: result.email,
              firstName: result.firstName,
              lastName: result.lastName,
            };
          }
          this.stateService.updateAccountsOnUserUpdate(result.account, result.email);
        }
        this.utilService.openSnackBar(`User "${result.firstName}" updated successfuly`, {
          position: 'center',
          variant: 'success'
        });
        this.stateService.loading = false;
      }
    });
  }

  private editBrand(brand: Brand): void {
    const dialogRef = this.dialog.open(BrandModalComponent, {
      data: {...brand},
      panelClass: 'management-modal',
      autoFocus: false
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: Brand) => {
      if (result) {
        const found = this.replaceRow(row => (row as Brand).brandKey === result.brandKey, result);
        if (found) {
          this.managementService.updateBrandInList({name: result.name, brandKey: result.brandKey});
          this.utilService.openSnackBar(`Brand "${result.name}" updated successfuly`, {
            position: 'center',
            variant: 'success'
          });
        }
        this.stateService.loading = false;
      }
    });
  }
}
